import React, { useState, useEffect } from "react";
import {
  Modal,
  Checkbox,
  InlineLoading,
  InlineNotification,
} from "@carbon/react";
import "./chatMembersModal.scss";
import { useGetChatMembers } from "../../hooks/useGetChatMembers";
import { useAddMemberToChat } from "../../hooks/useAddMemberToChat";
import { useRemoveMemberFromChat } from "../../hooks/useRemoveMemberFromChat";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import ReactGA from "react-ga4";

export default function ChatMembersModal({
  openModal,
  setOpenModal,
  chatId,
  setEditChatId,
  editChatName,
  setEditChatName,
  guid,
  // onMembersUpdated,
  // handleCreationStopped,
  // isNewClient = false,
}) {
  const [allMembers, setAllMembers] = useState([]);
  const [originalMembers, setOriginalMembers] = useState([]);
  const [originalPossible, setOriginalPossible] = useState([]);
  const [notification, setNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const mutationAllMembers = useGetChatMembers();
  const mutationAddMember = useAddMemberToChat();
  const mutationRemoveMember = useRemoveMemberFromChat();
  const { responseInfo } = useOutletContext();

  const { t } = useTranslation();

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "ChatMembersModal",
      firebase_screen_class: "ChatManagerTable",
    });
  }, []);

  useEffect(() => {
    if (guid && chatId) {
      mutationAllMembers.mutate({ guid, chatId });
    }
  }, [guid, chatId]);

  const {
    isPending: isChatMembersPending,
    isSuccess: isChatMembersSuccess,
    data: chatMembersData,
    error,
  } = mutationAllMembers;

  useEffect(() => {
    if (error) {
      setNotification({
        visibility: true,
        title: "authentication.notification.requestError500.title",
        subtitle: "authentication.notification.requestError500.serverError",
        kind: "error",
      });
    }
  }, [error]);

  useEffect(() => {
    if (isChatMembersSuccess) {
      const membersWithChecked = chatMembersData?.data?.data.members.map(
        (m) => ({
          ...m,
          checked: true,
        })
      );

      const possibleFiltered = chatMembersData?.data?.data.possible.map(
        (p) => ({
          ...p,
          checked: false,
        })
      );

      const merged = [...membersWithChecked, ...possibleFiltered];

      setAllMembers(merged);
      setOriginalMembers(chatMembersData?.data?.data.members);
      setOriginalPossible(chatMembersData?.data?.data.possible);
    }
  }, [isChatMembersSuccess, chatMembersData?.data?.data]);

  const handleCheckboxChange = (chataccid) => {
    setAllMembers((prev) =>
      prev.map((m) =>
        m.chataccid === chataccid ? { ...m, checked: !m.checked } : m
      )
    );
  };

  const handleSubmit = () => {
    const currentMembers = allMembers.filter((m) => m.checked);
    const currentPossible = allMembers.filter((m) => !m.checked);

    // const addedSelf = currentMembers.some((m) =>
    //   m.name.includes(responseInfo?.publicName)
    // );

    const oldMemberIds = new Set(originalMembers.map((m) => m.chataccid));
    const oldPossibleIds = new Set(originalPossible.map((m) => m.chataccid));

    const toAdd = currentMembers.filter((m) => oldPossibleIds.has(m.chataccid));
    const toRemove = currentPossible.filter((m) =>
      oldMemberIds.has(m.chataccid)
    );

    if (!toAdd.length && !toRemove.length) handleCloseModal();

    toAdd.forEach((item) => {
      mutationAddMember.mutate(
        { guid, chatId, chataccid: item.chataccid },
        {
          onSuccess: () => {
            // if (isNewClient) {
            //   if (addedSelf) {
            //     onMembersUpdated();
            //     setOpenModal(false);
            //     setEditChatId(null);
            //     setEditChatName("");
            //   } else {
            //     handleCloseModal();
            //   }
            // } else {
            handleCloseModal();
            // }
          },
          onError: (error) => {
            setNotification({
              visibility: true,
              title: "authentication.notification.requestError500.title",
              subtitle:
                "authentication.notification.requestError500.serverError",
              kind: "error",
            });
          },
        }
      );
    });

    toRemove.forEach((item) => {
      mutationRemoveMember.mutate(
        { guid, chatmemberid: item.chatmemberid },
        {
          onSuccess: () => {
            setOpenModal(false);
            setEditChatId(null);
            setEditChatName("");
          },
          onError: (error) => {
            setNotification({
              visibility: true,
              title: "authentication.notification.requestError500.title",
              subtitle:
                "authentication.notification.requestError500.serverError",
              kind: "error",
            });
          },
        }
      );
    });
  };

  const handleCloseModal = () => {
    // if (isNewClient) {
    //   handleCreationStopped();
    // }
    setOpenModal(false);
    setEditChatId(null);
    setEditChatName("");
  };

  return (
    <div>
      <Modal
        open={!!openModal}
        onRequestClose={handleCloseModal}
        modalHeading={`${t("main.chat.chatMembersModal.title", {
          chatName: editChatName,
        })}`}
        primaryButtonText={`${t("main.chat.chatMembersModal.edit")}`}
        secondaryButtonText={`${t("main.chat.chatMembersModal.cancel")}`}
        onRequestSubmit={handleSubmit}
        onSecondarySubmit={handleCloseModal}
        closeButtonLabel={`${t("main.modalText.close")}`}
      >
        {isChatMembersPending ? (
          <InlineLoading
            description={`${t("main.chat.chatMembersModal.loading")}`}
            iconDescription="Loading data..."
          />
        ) : (
          allMembers.map((member) => (
            <Checkbox
              key={member.chataccid}
              id={`member-${member.chataccid}`}
              labelText={member.name || `Unnamed (${member.chataccid})`}
              checked={member.checked}
              onChange={() => handleCheckboxChange(member.chataccid)}
            />
          ))
        )}
        {notification.visibility && (
          <InlineNotification
            title={t(notification.title)}
            subtitle={t(notification.subtitle)}
            kind={notification.kind}
            onClose={() =>
              setNotification({
                visibility: false,
                title: "",
                subtitle: "",
                kind: "",
              })
            }
            lowContrast={true}
            aria-label={t("authentication.notification.closeNotification")}
            className="notification"
          />
        )}
      </Modal>
    </div>
  );
}
